/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type CreateItpInput } from '@therapy/shared-validators';

import { apiClient } from './client.js';

import type { Page } from './resources.js';

export type { CreateItpInput, Page };

export type ItpStatus = 'draft' | 'active' | 'under_review' | 'completed' | 'discontinued';

/** Query params for the paginated plan list (offset/limit + search + status). */
export interface PlansPageParams {
  readonly limit: number;
  readonly offset: number;
  readonly q?: string | undefined;
  readonly status?: ItpStatus | undefined;
}
export type GoalStatus =
  | 'not_started'
  | 'emerging'
  | 'in_progress'
  | 'achieved'
  | 'maintenance'
  | 'discontinued';

/** Therapy discipline a taxonomy goal belongs to (BRD M01 goal taxonomy). */
export type GoalDomain = 'SLT' | 'OT' | 'ABA' | 'PT' | 'SPECED';

/** Standardised measurement method for a goal (BRD M01 #4). */
export type MeasurementType =
  | 'frequency'
  | 'percent_accuracy'
  | 'duration'
  | 'latency'
  | 'trials_to_criterion'
  | 'rating_scale'
  | 'independence_level'
  | 'prompt_level'
  | 'anecdotal';

/** One entry in a goal's append-only status-change log (BRD M01 #6). */
export interface GoalStatusChange {
  readonly id: string;
  readonly fromStatus: GoalStatus | null;
  readonly toStatus: GoalStatus;
  readonly note: string | null;
  readonly changedBy: string | null;
  readonly changedAt: string;
}

/** An evidence-based goal from the shared starter library (read-only). */
export interface TaxonomyGoal {
  readonly id: string;
  readonly domain: GoalDomain;
  readonly code: string;
  readonly title: string;
  readonly description: string | null;
  readonly defaultMeasurement: string | null;
}

/** A teaching step beneath a sub-target — the leaf tier (BRD M01 #3). */
export interface PlanStepItem {
  readonly id: string;
  readonly title: string;
  readonly done: boolean;
  readonly sortOrder: number;
}

/** A sub-target beneath a goal (P1-15), with its teaching steps. */
export interface PlanStep {
  readonly id: string;
  readonly title: string;
  readonly done: boolean;
  readonly sortOrder: number;
  readonly items: readonly PlanStepItem[];
}

export interface PlanSummary {
  readonly id: string;
  readonly clientId: string;
  readonly name: string;
  readonly programType: string | null;
  readonly startDate: string | null;
  readonly reviewDate: string | null;
  readonly status: ItpStatus;
  readonly currentVersion: number;
  readonly progressPct: number;
}

/** Plan summary enriched with current-version goal titles (list cards). */
export interface PlanListItem extends PlanSummary {
  readonly goalTitles: readonly string[];
}

export interface PlanGoal {
  readonly id: string;
  readonly taxonomyId: string | null;
  readonly title: string;
  readonly category: string | null;
  readonly domain: GoalDomain | null;
  readonly measurementType: MeasurementType | null;
  readonly measurementMethod: string | null;
  readonly baseline: string | null;
  readonly target: string | null;
  readonly reviewDate: string | null;
  readonly status: GoalStatus;
  readonly progressPct: number;
  readonly sortOrder: number;
  readonly approachTags: readonly string[];
  readonly steps: readonly PlanStep[];
}

export interface PlanDetail extends PlanSummary {
  readonly goals: readonly PlanGoal[];
}

export const itpApi = {
  async taxonomy(): Promise<readonly TaxonomyGoal[]> {
    const { data } = await apiClient.get<readonly TaxonomyGoal[]>('/itps/taxonomy');
    return data;
  },
  async list(): Promise<readonly PlanListItem[]> {
    const { data } = await apiClient.get<readonly PlanListItem[]>('/itps');
    return data;
  },
  async listPage(params: PlansPageParams): Promise<Page<PlanListItem>> {
    const { data } = await apiClient.get<Page<PlanListItem>>('/itps/page', { params });
    return data;
  },
  async create(payload: CreateItpInput): Promise<PlanDetail> {
    const { data } = await apiClient.post<PlanDetail>('/itps', payload);
    return data;
  },
  async getOne(id: string): Promise<PlanDetail> {
    const { data } = await apiClient.get<PlanDetail>(`/itps/${id}`);
    return data;
  },
  async updateGoalProgress(
    planId: string,
    goalId: string,
    patch: {
      readonly status?: GoalStatus | undefined;
      readonly progressPct?: number | undefined;
      readonly note?: string | undefined;
    },
  ): Promise<PlanDetail> {
    const { data } = await apiClient.patch<PlanDetail>(
      `/itps/${planId}/goals/${goalId}/progress`,
      patch,
    );
    return data;
  },
  async goalHistory(planId: string, goalId: string): Promise<readonly GoalStatusChange[]> {
    const { data } = await apiClient.get<readonly GoalStatusChange[]>(
      `/itps/${planId}/goals/${goalId}/history`,
    );
    return data;
  },
  async toggleStep(
    planId: string,
    goalId: string,
    stepId: string,
    done: boolean,
  ): Promise<PlanDetail> {
    const { data } = await apiClient.patch<PlanDetail>(
      `/itps/${planId}/goals/${goalId}/steps/${stepId}`,
      { done },
    );
    return data;
  },
  async toggleStepItem(
    planId: string,
    target: {
      readonly goalId: string;
      readonly stepId: string;
      readonly itemId: string;
      readonly done: boolean;
    },
  ): Promise<PlanDetail> {
    const { goalId, stepId, itemId, done } = target;
    const { data } = await apiClient.patch<PlanDetail>(
      `/itps/${planId}/goals/${goalId}/steps/${stepId}/items/${itemId}`,
      { done },
    );
    return data;
  },
};
